import React, { Fragment, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ReactComponent as CloseIcon } from '@pkg/assets/icons/v2/close.svg';
import { ReactComponent as CryptoIcon } from '@pkg/assets/icons/v2/crypto/bitcoin-mono.svg';
import { ReactComponent as EthereumIcon } from '@pkg/assets/icons/v2/ethereum.svg';
import { ReactComponent as OpenSeaIcon } from '@pkg/assets/icons/v2/opensea.svg';
import { ReactComponent as RenameIcon } from '@pkg/assets/icons/v2/rename.svg';
import { ReactComponent as RevisualizeIcon } from '@pkg/assets/icons/v2/revisualize.svg';
import { ButtonDeprecated, CircleButton, Icons, YatFadeIn, YatTile } from '@pkg/components';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { useLockBodyScroll } from '@app/helpers';
import { setMobileTransferModalIsOpen } from '@app/store/actions/dashboard';
import { openRenameYatSidePanel, setNftPanelOpen } from '@app/store/actions/nft';
import { setTransferPanelOpen } from '@app/store/actions/transfers';
import { toggle2faMintingPopup } from '@app/store/slices';

import { Typography } from '../../DesignSystem';

import './MobileTransferModal.scss';

export const YatDashboardMobileTransferModal = () => {
    const { t } = useTranslation(['dashboard', 'words', 'common']);
    const dispatch = useAppDispatch();
    const user = useAppSelector((state) => state.user);
    const { mobileTransferModalIsOpen } = useAppSelector((state) => state.dashboard);
    const { openSeaUrl } = useAppSelector((state) => state.nft);
    const {
        currentEmoji,
        currentEmojiMinted,
        currentRhythmScore,
        visualizerIsLocked,
        visualizerNameIsLocked,
        visualizerUrl,
        currentFree,
        blockedUntil,
    } = useAppSelector((state) => state.userEmojis);
    const { features } = useAppSelector((state) => state.featureFlags);

    const [modalAriaHidden, setModalAriaHidden] = useState(true); //setting to false before to prevent the flash on load (not sure if this works 100% tbh i feel like it still fades in a bit TODO? )
    const [twoFAEnabled, setTwoFAEnabled] = useState<boolean>();
    const [mintOrTransferBlocked, setMintOrTransferBlocked] = useState(false);

    useLockBodyScroll(mobileTransferModalIsOpen);
    const userAccount = user?.userAccount?.user;

    useEffect(() => {
        if (userAccount) {
            setTwoFAEnabled(!!userAccount.two_factor_auth);
        }
    }, [userAccount]);
    const closeSidebar = () => {
        dispatch(setMobileTransferModalIsOpen(false));
    };

    const openTransferMenu = () => {
        dispatch(setTransferPanelOpen(true));
    };

    const handleMintYat = () => {
        dispatch(twoFAEnabled ? setNftPanelOpen(true) : toggle2faMintingPopup(true));
    };

    const handleRevisualize = () => {
        if (visualizerUrl) {
            window.location.href = visualizerUrl;
        }
    };

    const onClickOpenSea = () => {
        if (openSeaUrl) {
            window.open(openSeaUrl, '_blank');
            dispatch(setNftPanelOpen(false));
        }
    };

    useEffect(() => {
        setModalAriaHidden(!mobileTransferModalIsOpen);
    }, [mobileTransferModalIsOpen]);

    useEffect(() => {
        if (currentFree) {
            setMintOrTransferBlocked(true);
        } else if (blockedUntil) {
            const now = Date.parse(new Date().toISOString());
            const blocked = Date.parse(blockedUntil);
            setMintOrTransferBlocked(blocked > now);
        } else {
            setMintOrTransferBlocked(false);
        }
    }, [currentFree, blockedUntil]);

    const openRenameYat = () => {
        dispatch(openRenameYatSidePanel());
    };

    return (
        <YatFadeIn appear={mobileTransferModalIsOpen} classes="yat-dashboard-mobile-transfer-modal__outer">
            <nav
                id="yat-dashboard-mobile-transfer-modal"
                className="yat-dashboard-mobile-transfer-modal"
                aria-hidden={modalAriaHidden}
            >
                <div className="yat-dashboard-mobile-transfer-modal__top">
                    <YatTile
                        yat={currentEmoji}
                        rhythmScore={currentRhythmScore}
                        glow={true}
                        classes="yat-dashboard-mobile-transfer-modal__current-yat yat-dashboard-mobile-top-bar__current-yat"
                    />
                    <CircleButton
                        onClick={closeSidebar}
                        ariaLabel={t('close_menu')}
                        size="large"
                        classes="yat-dashboard-mobile-transfer-modal__close"
                    >
                        <CloseIcon aria-hidden="true" />
                    </CircleButton>
                </div>
                <div className="yat_dashboard-mobile-content">
                    {!currentEmojiMinted && (
                        <Fragment>
                            <div className="yat_dashboard-mobile-content__item">
                                <Typography type="headline" size={20} classes={['yat_dashboard-mobile-content__title']}>
                                    {t('transfer_yat')}
                                </Typography>
                                <Typography type="paragraph" size={16} classes={['yat_dashboard-mobile-content__text']}>
                                    {t('yat_transfer_mobile_copy')}
                                </Typography>

                                <ButtonDeprecated
                                    color="tertiary"
                                    fluid
                                    icon={<Icons.TransferArrows />}
                                    onClick={openTransferMenu}
                                    size="large"
                                    disabled={mintOrTransferBlocked}
                                >
                                    {t('transfer_yat')}
                                </ButtonDeprecated>
                            </div>

                            <div className="yat_dashboard-mobile-divider-line" />

                            <div className="yat_dashboard-mobile-content__item">
                                <Typography type="headline" size={20} classes={['yat_dashboard-mobile-content__title']}>
                                    {t('manage_nft')}
                                </Typography>
                                <Typography type="paragraph" size={16} classes={['yat_dashboard-mobile-content__text']}>
                                    {t('yat_manage_nft_mobile_text')}
                                </Typography>
                                <ButtonDeprecated
                                    classes={['yat_dashboard-mobile-content__managenft']}
                                    color="tertiary"
                                    fluid
                                    icon={<EthereumIcon />}
                                    onClick={handleMintYat}
                                    size="large"
                                    disabled={mintOrTransferBlocked}
                                >
                                    {t('mint_yat')}
                                </ButtonDeprecated>
                            </div>
                        </Fragment>
                    )}

                    {currentEmojiMinted && (
                        <Fragment>
                            <div className="yat_dashboard-mobile-content__item">
                                <div onClick={() => null}>
                                    <Typography
                                        classes={['yat_dashboard-mobile-content__title']}
                                        size={20}
                                        type="headline"
                                    >
                                        {t('transfer_yat')}
                                    </Typography>
                                    <Typography
                                        size={16}
                                        classes={[
                                            'yat_dashboard-mobile-content__text',
                                            'yat_dashboard-mobile-content__transfer-text',
                                        ]}
                                        type="paragraph"
                                    >
                                        {t('yat_nft_mint_mobile_disabled_text')}
                                    </Typography>
                                    <Typography
                                        classes={['yat_dashboard-mobile-content__link']}
                                        size={16}
                                        type="paragraph"
                                    >
                                        {t('common:learn_more')}
                                    </Typography>
                                </div>
                                <ButtonDeprecated
                                    size="large"
                                    color="tertiary"
                                    fluid
                                    icon={<Icons.TransferArrows />}
                                    disabled={mintOrTransferBlocked}
                                >
                                    {t('transfer_yat')}
                                </ButtonDeprecated>
                            </div>

                            <div className="yat_dashboard-mobile-divider-line" />

                            <div className="yat_dashboard-mobile-content__item">
                                <Typography type="headline" size={20} classes={['yat_dashboard-mobile-content__title']}>
                                    {t('manage_nft')}
                                </Typography>
                                <Typography type="paragraph" size={16} classes={['yat_dashboard-mobile-content__text']}>
                                    {t('yat_nft_mint_mobile_text')}
                                </Typography>
                                {features.enableYatRemint && !visualizerIsLocked && (
                                    <ButtonDeprecated
                                        classes={['yat_dashboard-mobile-content__managenft']}
                                        color="tertiary"
                                        fluid
                                        icon={<RevisualizeIcon />}
                                        size="large"
                                        onClick={handleRevisualize}
                                    >
                                        {t('revisualize_yat')}
                                    </ButtonDeprecated>
                                )}

                                {features.enableYatRemint && !visualizerNameIsLocked && (
                                    <ButtonDeprecated
                                        classes={['yat_dashboard-mobile-content__managenft']}
                                        color="tertiary"
                                        fluid
                                        icon={<RenameIcon />}
                                        onClick={openRenameYat}
                                        size="large"
                                    >
                                        {t('rename_yat')}
                                    </ButtonDeprecated>
                                )}

                                <ButtonDeprecated
                                    classes={['yat_dashboard-mobile-content__opensea']}
                                    color="tertiary"
                                    fluid
                                    icon={<OpenSeaIcon />}
                                    onClick={onClickOpenSea}
                                    size="large"
                                >
                                    {t('view_on_opensea')}
                                </ButtonDeprecated>
                            </div>
                        </Fragment>
                    )}

                    <div className="yat_dashboard-mobile-divider-line" />

                    <div className="yat_dashboard-mobile-content__item">
                        <Typography type="headline" size={20} classes={['yat_dashboard-mobile-content__title']}>
                            {t('menu_label_crypto')}
                        </Typography>
                        <Typography type="paragraph" size={16} classes={['yat_dashboard-mobile-content__text']}>
                            {t('menu_description_crypto')}
                        </Typography>
                        <Link to="/paywithyat">
                            <ButtonDeprecated
                                classes={['yat_dashboard-mobile-content__managenft']}
                                color="tertiary"
                                fluid
                                icon={<CryptoIcon />}
                                size="large"
                            >
                                {t('wallet_manager')}
                            </ButtonDeprecated>
                        </Link>
                    </div>
                </div>
            </nav>
        </YatFadeIn>
    );
};
